import logrocketService from './services/logrocket.service';
import datadogService from './services/datadog.service';
import amplitudeService from './services/amplitude.service';
import type { InitConfig } from './types';

export { HOST_QUERY_TYPE, HOST_REPLY_TYPE, installHostListener, queryHost } from './observabilityHandshake';
export type { HostReply, LogRocketHostReply, DatadogHostReply } from './observabilityHandshake';
export { installAppActionBreadcrumbs, truncateAppActionValue } from './appActionBreadcrumbs';

export default {
  isInitialized: false,
  isDev: false,
  datadogDebug: false,
  async init(config: InitConfig) {
    this.isDev = config.isDev;
    this.datadogDebug = config.datadogDebug ?? config.isDev;

    if (config.logrocket.enabled) {
      logrocketService.init(config.logrocket.appID, config.logrocket.options);
    }

    if (config.datadog?.enabled) {
      datadogService.init({
        mode: config.datadog.mode,
        applicationId: config.datadog.applicationId,
        clientToken: config.datadog.clientToken,
        service: config.datadog.service,
        env: config.datadog.env,
        version: config.datadog.version,
        sessionSampleRate: config.datadog.sessionSampleRate,
        sessionReplaySampleRate: config.datadog.sessionReplaySampleRate,
        useCrossSiteSessionCookie: config.datadog.useCrossSiteSessionCookie,
        trackSessionAcrossSubdomains: config.datadog.trackSessionAcrossSubdomains,
        trackingConsent: config.datadog.trackingConsent
      });
    }

    if (config.amplitude?.enabled) {
      amplitudeService.init(config.amplitude.apiKey);
    }

    if (this.datadogDebug) {
      // eslint-disable-next-line no-console
      console.log('[datadog] init', {
        enabled: config.datadog?.enabled ?? false,
        mode: config.datadog?.mode ?? null,
        hasClientToken: Boolean(config.datadog?.clientToken),
        service: config.datadog?.service ?? null,
        env: config.datadog?.env ?? null,
        version: config.datadog?.version ?? null,
        ...datadogService.debugStatus()
      });
    }

    this.isInitialized = true;
  },
  identify(uid: string, traits: any) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.identify(uid, traits);
    datadogService.identify(uid);
    amplitudeService.identify(uid, traits);
  },
  setTrackingConsent(consent: 'granted' | 'not-granted') {
    if (!this.isInitialized) {
      return;
    }
    datadogService.setTrackingConsent(consent);
  },
  log(...params: any[]) {
    // eslint-disable-next-line no-console
    console.log(...params);
  },
  warn(...params: any[]) {
    console.warn(...params);
  },
  info(...params: any[]) {
    if (this.isDev) {
      // eslint-disable-next-line no-console
      console.log(...params);
    }

    if (!this.isInitialized) {
      return;
    }

    logrocketService.info(...params);

    // Logs-only mode receives structured `metric()` records only.
    if (datadogService.isLogsOnly) {
      return;
    }

    const message = params
      .map(p =>
        typeof p === 'string'
          ? p
          : (() => {
              try {
                return JSON.stringify(p);
              } catch {
                return String(p);
              }
            })()
      )
      .join(' ');

    // Extract plain objects as structured Datadog context so metadata is not
    // lost to the flattened message string. Arrays, Errors, and primitives stay
    // in the message only — Datadog's context expects a key-value record.
    const contextObjects = params.filter(
      p => typeof p === 'object' && p !== null && !Array.isArray(p) && !(p instanceof Error)
    );
    if (contextObjects.length > 0) {
      datadogService.info(message, Object.assign({}, ...contextObjects));
    } else {
      datadogService.info(message);
    }
  },
  error(...params: any[]) {
    console.error(...params);

    if (!this.isInitialized) return;

    const firstArg = params[0];

    // Callers pass a '[Component] operation failed' message plus `{ error }` metadata.
    // Report the Error itself so Datadog keeps its real stack and message, and send the
    // remaining metadata as context — an Error left inside context only serialises to {}.
    const contextObjects = params.filter(
      p => typeof p === 'object' && p !== null && !Array.isArray(p) && !(p instanceof Error)
    );
    const context: Record<string, unknown> = Object.assign({}, ...contextObjects);
    const reportedError =
      params.find(p => p instanceof Error) ?? (context.error instanceof Error ? context.error : undefined);

    if (reportedError) {
      if (context.error === reportedError) {
        delete context.error;
      }
      if (typeof firstArg === 'string') {
        context.logMessage = firstArg;
      }
    }

    if (Object.keys(context).length > 0) {
      datadogService.error(reportedError ?? firstArg, context);
    } else {
      datadogService.error(reportedError ?? firstArg);
    }

    if (firstArg instanceof Error) {
      logrocketService.captureException(firstArg);
    } else if (typeof firstArg === 'string') {
      logrocketService.captureMessage(firstArg);
    } else {
      let message: string;
      try {
        message = JSON.stringify(firstArg) ?? String(firstArg ?? '');
      } catch {
        message = String(firstArg ?? '');
      }
      logrocketService.captureMessage(message);
    }
  },
  track(eventName: string, props = {} as any) {
    if (!this.isInitialized) {
      return;
    }

    const eventProperties = Object.keys(props).reduce((acc, key) => {
      acc[key] = [null, undefined].includes(props[key]) ? '' : props[key];
      return acc;
    }, {});

    logrocketService.track(eventName, eventProperties);
  },
  trackAmplitude(eventName: string, props = {} as any) {
    if (!this.isInitialized) {
      return;
    }
    amplitudeService.track(eventName, props);
  },
  captureException(error: Error) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.captureException(error);
    datadogService.error(error);
  },
  captureMessage(message: string) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.captureMessage(message);
  },
  addFeatureFlag(flagName: string, variant: string) {
    if (!this.isInitialized) {
      return;
    }

    datadogService.addFeatureFlag(flagName, variant);
  },
  addMetadata(key: string, value: any) {
    if (!this.isInitialized) {
      return;
    }

    datadogService.addMetadata(key, value);
  },
  // Structured log to Datadog only. `name` is the log message, `attributes` the context.
  metric(name: string, attributes: Record<string, unknown>) {
    if (!this.isInitialized) {
      if (this.datadogDebug) {
        console.warn('[metric] dropped, logger not initialised', name);
      }
      return;
    }
    if (this.datadogDebug) {
      // eslint-disable-next-line no-console
      console.log('[metric]', name, attributes);
      if (!datadogService.isInitialized) {
        console.warn('[metric] not sent, Datadog not initialised', name, datadogService.debugStatus());
      }
    }
    datadogService.info(name, attributes);
  }
};
